import { EcEnv } from '@castlery/config';
import { CMS_PATHS } from '@castlery/modules-cms-domain';
import {
  BlogBannerStoryblok,
  BlogPageStoryblok,
  FooterV2Storyblok,
  GlobalNavStoryblok,
  MenuVariantStoryblok,
  MoticeGroupV2Storyblok,
  PageStoryblok,
  PdpWidgetsStoryblok,
  PlaDataBucketTemplateV2Storyblok,
  PlaPageStoryblok,
  SalePagesStoryblok,
  ShopTheLookDataV2Storyblok,
  TermsWithVersionStoryblok,
  VisualSalePageStoryblok,
} from '@castlery/types';
import { ISbStoriesParams, ISbStoryData, ISbStoryParams, StoryblokClient } from '@storyblok/react';
import moment from 'moment-timezone';
import { fetchKnightApi } from '../fetch';
import { isOutdated } from '../utils';
import { SbBaseService } from './sbBaseService';
import { logger } from '@castlery/observability';

// if (typeof global.fetch !== 'undefined') {
//   const originalFetch = global.fetch;
//   global.fetch = async function (...args) {
//     console.log('[API Request]', args[0], args[1]); // 记录请求信息

//     try {
//       const response = await originalFetch.apply(this, args);
//       // 克隆响应以便可以读取 body
//       const clone = response.clone();
//       const body = await clone.json();
//       // console.log('[API Response]', body);
//       return response;
//     } catch (error) {
//       console.error('[API Error]', error);
//       throw error;
//     }
//   };
// }

export const sbContentTypes = {
  BlogPage: 'Blog Page',
  Page: 'page',
  VisualSalePage: 'Visual Sale Page',
} as const;

export class SbService extends SbBaseService {
  constructor({ client }: { client: StoryblokClient }) {
    super({ client });
  }

  private _normalizeUrl(url: string): string {
    if (!url) return '';
    let normalized = url.startsWith('/') ? url.slice(1) : url;
    normalized = normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
    const questionMarkIndex = normalized.indexOf('?');
    if (questionMarkIndex !== -1) {
      normalized = normalized.substring(0, questionMarkIndex);
    }
    return normalized.toLowerCase();
  }

  private _extractStoryUrl(story: ISbStoryData): string | null {
    const path = story.path || story.slug;
    if (!path) return null;

    let url = path;
    if (story.full_slug.startsWith(this.region)) {
      if (path.startsWith(this.region)) {
        url = path.replace(this.region, '');
      }
    }
    return this._normalizeUrl(url);
  }

  async getGlobalNotice(_params?: Partial<ISbStoriesParams>) {
    const slug = `${this.region}general-configuration/universal-config-new-joyboy/notice`;
    const data = await this.getStoryWithCache<MoticeGroupV2Storyblok>(slug);
    if (!data) {
      logger.error('No notice data found', { slug });
      return;
    }
    const { limit_num, notice, notice_mobile } = data.content;
    if (!notice || !limit_num || !notice_mobile) {
      logger.error('Invalid notice data structure', { slug });
      return;
    }
    return {
      noticeBarData: notice?.filter((item) => !isOutdated(item.published_at, item.ended_at)).slice(0, +limit_num) || [],
      noticeBarMobileData:
        notice_mobile?.filter((item) => !isOutdated(item.published_at, item.ended_at)).slice(0, +limit_num) || [],
    };
  }
  async getRawSalePages(params?: Partial<ISbStoriesParams>) {
    const slug = `${this.region}${CMS_PATHS.SALE_PAGES}`;
    try {
      const rawData = await this.getStoryWithCache<SalePagesStoryblok>(slug, params);
      if (!rawData) {
        logger.error('No sale pages data found', { slug });
        return {
          data: null,
          error: new Error('No sale pages data found'),
        };
      }
      const { seo_pages, sale_pages } = rawData.content;
      const pages = sale_pages?.concat(seo_pages || []) || [];

      const filterData = pages?.reduce((pre: typeof pages, item) => {
        const targetIndex = pre.findIndex((page) => page.path === item.path);
        if (targetIndex !== -1) {
          if (!isOutdated(item.published_at, item.ended_at)) {
            pre.splice(targetIndex, 1, item);
          }
        } else {
          pre.push(item);
        }

        return pre;
      }, [] as typeof pages);
      return {
        data: filterData,
        error: null,
      };
    } catch (error) {
      logger.error('Failed to fetch sale pages', { slug, error });
      return {
        data: null,
        error: error as Error,
      };
    }
  }
  // TODO @Wcdaren 让业务维持一个 archived sale page 的 content 就好了
  async getSalePages(params?: Partial<ISbStoriesParams>) {
    const { data, error } = await this.getRawSalePages(params);
    if (error) {
      // 这里不需要再记录日志，getRawSalePages 已经记录过了
      return;
    }

    const optimizedRes = data.map((item) => {
      return {
        key: item.key,
        query: item.query,
        name: item.name,
        metaTitle: item.name,
        metaDescription: '',
        url: item.path,
        _uid: item._uid,
        isSeoPage: item.is_seo_page,
        outdatedUrls: item.outdated_urls,
        query_deliver_before: item.query_deliver_before,
      };
    });
    return optimizedRes;
  }

  async getPages(params?: Omit<ISbStoriesParams, 'content_type'>) {
    // TODO 这里需要carl 确认下 这里是否需要添加路径限制
    const res = await this.getAllWithCache(
      'cdn/stories',
      {
        ...params,
        content_type: sbContentTypes.Page,
        starts_with: this.region,
      },
      {
        tags: [sbContentTypes.Page],
      }
    );
    // 这里要不要使用 timer 来过滤
    return res || [];
  }

  async getVisualSalePages(params?: Omit<ISbStoriesParams, 'content_type'>) {
    const { stories } = await this.getStoriesWithCache<VisualSalePageStoryblok>(
      {
        ...params,
        content_type: sbContentTypes.VisualSalePage,
        starts_with: `${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}/general-content-v2/sale-pages/visual-sale-pages-folder/`,
      },
      {
        tags: [sbContentTypes.VisualSalePage],
      }
    );
    return stories || [];
  }

  async getGlobalNav(params?: Partial<ISbStoriesParams>) {
    const slug = `${this.region}${CMS_PATHS.GLOBAL_NAV}`;
    const res = await this.getStoryWithCache<GlobalNavStoryblok>(slug, params);
    if (!res) {
      logger.error('No global nav data found', { slug });
      return;
    }

    return res?.content.items;
  }

  async getGlobalMenuGroupMenu(params?: Partial<ISbStoriesParams>) {
    const slug = `${this.region}${CMS_PATHS.MENU.OUTER}`;
    const data = await this.getStoryWithCache<MenuVariantStoryblok>(slug, {
      ...params,
    });
    if (!data) {
      logger.error('No menu data found', { slug });
      return;
    }
    const inDateRangeData = data.content.menu_component_data?.map((item) => ({
      ...item,
      blocks: item?.blocks?.filter((block) => !isOutdated(block.published_at, block.ended_at)),
    }));
    const optimizedData = inDateRangeData?.map((item) => ({
      slug: item.slug,
      blocks:
        item?.blocks?.length && item?.blocks?.length > 0
          ? item.blocks.map(({ link, title, image_url, action_text, permalink }) => ({
              link,
              title,
              image_url,
              action_text,
              permalink,
            }))
          : [],
      disable: item?.disable === true,
    }));
    return optimizedData;
  }

  async getGlobalHeader() {
    const [originalMenu, globalNavData, currentSalePages, outerMenuData, collectionsData] = await Promise.all([
      fetchKnightApi({ slug: CMS_PATHS.TAXONOMIES.MENU }),
      this.getGlobalNav(),
      this.getSalePages(),
      this.getGlobalMenuGroupMenu(),
      fetchKnightApi({ slug: CMS_PATHS.TAXONOMIES.COLLECTIONS }),
    ]);

    return {
      originalMenu: originalMenu || { children: [] },
      globalNavData: globalNavData || [],
      currentSalePages: currentSalePages || [],
      outerMenuData: outerMenuData || [],
      collectionsData: collectionsData || [],
    };
  }

  async getGlobalFooter(params?: Partial<ISbStoriesParams>) {
    const slug = `${this.region}${CMS_PATHS.FOOTER}`;
    const data = await this.getStoryWithCache<FooterV2Storyblok>(slug, params);
    if (!data) {
      logger.error('No footer data found', { slug });
      return;
    }
    const { bottomList, mobileList, socialList, list } = data.content;
    return {
      footerData: list || [],
      socialList: socialList || [],
      bottomList: bottomList || [],
      mobileList: mobileList || [],
    };
  }

  async getBlogPosts(params?: Partial<ISbStoriesParams>) {
    const { stories } = await this.getStoriesWithCache<BlogPageStoryblok>({
      starts_with: this.region + 'general-content-v2/ugc-pgc/blog/',
      content_type: sbContentTypes.BlogPage,
      page: 1,
      per_page: 9,
      ...(params || {}),
    });

    return stories.map((item) => {
      const blogBanner = item.content?.body?.find((block) => block.component === 'blog-banner') as BlogBannerStoryblok;

      return {
        key: String(item.id),
        bannerBackgroundImage: blogBanner?.image?.[0].desktop_url || '',
        name: item.name,
        url: '/' + item.slug,
        publishedAt: item.published_at === null ? '' : moment(item.published_at).format('MMM D, YYYY'),
      };
    });
  }
  async getBlogPostsOnClient(params?: Partial<ISbStoriesParams>) {
    const { stories } = await this.getStoriesWithoutCache<BlogPageStoryblok>({
      starts_with: this.region + 'general-content-v2/ugc-pgc/blog/',
      content_type: sbContentTypes.BlogPage,
      page: 1,
      per_page: 9,
      ...(params || {}),
    });
    return stories.map((item) => {
      const blogBanner = item.content?.body?.find((block) => block.component === 'blog-banner') as BlogBannerStoryblok;

      return {
        key: String(item.id),
        bannerBackgroundImage: blogBanner?.image?.[0].desktop_url || '',
        name: item.name,
        url: '/' + item.slug,
        publishedAt: item.published_at === null ? '' : moment(item.published_at).format('MMM D, YYYY'),
      };
    });
  }
  async getBlogPost(slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithCache<BlogPageStoryblok>(
      `${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}/general-content-v2/ugc-pgc/blog/${slug}`,
      params,
      {
        tags: [`blog/${slug}`],
      }
    );
  }

  async getPlaLayout(variant: string, params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithCache<PlaPageStoryblok>(
      `${this.region}general-content/product-pages/pla-layout/${variant}`,
      {
        ...params,
      },
      {
        tags: [`${this.region}general-content/product-pages/pla-layout/${variant}`],
      }
    );
  }

  async getPlaDataBucket(slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithCache<PlaDataBucketTemplateV2Storyblok>(
      `${this.region}general-content/product-pages/pla-bucket/${slug}`,
      params,
      {
        tags: [`${this.region}general-content/product-pages/pla-bucket/${slug}`],
      }
    );
  }

  async getPlaDataBucketWithoutCache(slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStory<PlaDataBucketTemplateV2Storyblok>(
      `${this.region}general-content/product-pages/pla-bucket/${slug}`,
      params
    );
  }

  async getWidgets(slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithBothCache<PdpWidgetsStoryblok>(`${this.region}${slug}`, params, {
      tags: [`${this.region}${slug}`],
    });
  }

  async getShopTheLook(params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithCache<ShopTheLookDataV2Storyblok>(
      `${this.region}${CMS_PATHS.SHOP_THE_LOOK}`,
      params,
      {
        tags: [CMS_PATHS.SHOP_THE_LOOK],
      }
    );
  }

  // TODO @Wcdaren 到时好好思考下 怎么处理 getAllStories 如果放成 server only
  async getRestPage(slug: string) {
    const allStories = await this.getAllFromGeneralContentV2();
    const marketPathArr = ['sg/', 'us/', 'au/', 'ca/', 'uk/'];
    const page = allStories?.find(({ slug: rawSlug, path }) => {
      if (path !== null) {
        if (marketPathArr.some((prefix) => path?.startsWith(prefix))) {
          const matchedPrefix = marketPathArr.find((prefix) => path?.startsWith(prefix));
          if (matchedPrefix) {
            const newPath = path?.slice(matchedPrefix.length);
            if (newPath === slug) {
              return true;
            }
          }
        }
      } else {
        if (rawSlug === slug) {
          return true;
        }
      }
    });
    return page;
  }

  async getSpecificPage(full_slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStoryWithCache<PageStoryblok>(`${full_slug}`, params, {
      tags: [`${full_slug}`],
    });
  }

  async getSpecificPageWithoutCache(full_slug: string, params?: Partial<ISbStoryParams>) {
    return await this.getStory<PageStoryblok>(`${full_slug}`, params);
  }
}
